// --- Dependencies
import React, {useCallback, useState} from 'react';
import {useSelector} from 'react-redux';
import {debounce} from 'lodash';
// --- Components
import {InputLabel} from '../../../../../../components/InputLabel/InputLabel';
import {MultiSelect} from '@dtci/spellbook/MultiSelect';
import DataGrid from '@dtci/spellbook/DataGrid';
import Button from '@dtci/spellbook/Button';
// --- Hooks
import {useAgGridApi} from '../../../../../../components/AGGrid/hooks';
import {useSharedSectionsActions} from '../../../../../../store/sharedSections/useSharedSections';
import {useLinkedProductsActions} from '../../../../../../store/linkedProducts/useProducts';
// --- Config
import * as columnDefs from '../../../../../../components/AGGrid/config/columns-def';
// --- Styles
import './styles.scss';
import styles from '../../../../../../components/AGGrid/details-table.module.scss';
// --- Utils
import {fetchLinkedProducts, fetchProductOptions, fetchProducts} from '../../../../utils';
import {useLoading} from '../../../../../../utils/hooks/useLoading';
import {datastageApi} from '../../../../../../api';
import {updateSharedSections} from '../../../../../../store/sharedSections/actions';
import {useLinkedProductsDialog} from '../../../../../../components/dialogs/dialogs/linkedProducts';
import {useClearanceMemoTitlesActions} from '../../../../../../store/clearanceMemoTitles/useClearanceMemoTitles';
import {DetailsModal} from "../../../../../../components/DetailsModal";
import {MemoForm} from "../../../../../../components/ClearanceMemo/memo-form";
import {EditSectionModal} from "../../../../../../components/ClearanceMemo/memo-form/edit-modal";
import {useLinkedProductsGridUtils} from "./utils/gridUtils";

/**
 * ProductInput Component
 * Handles the product selection input.
 *
 * @param {Object} props - Component props.
 * @param {Array} props.productOptions - List of product options for the dropdown.
 * @param {Function} props.loadOptions - Function to load product options asynchronously.
 * @param {Array} props.selectedProducts - Currently selected products.
 * @param {Function} props.onChange - Callback function to handle product selection changes.
 * @returns {JSX.Element} - Rendered ProductInput component.
 */
export const ProductInput = ({productOptions, loadOptions, selectedProducts, onChange, onClick}) => {
    return (
        <div className="products-container" style={{display: 'flex', alignItems: 'center'}}>
            <InputLabel label="Products">
                <MultiSelect
                    className="products-input"
                    async
                    name="products"
                    defaultOptions={productOptions}
                    loadOptions={loadOptions}
                    useMenuPortal
                    isSearchable
                    isClearable
                    value={selectedProducts}
                    onChange={onChange}
                    style={{width: '300px'}}
                />
            </InputLabel>
            <Button
                style={{paddingTop: '15px'}}
                iconType="fa-solid fa-link"
                labelText="add"
                styleType="link"
                disabled={!selectedProducts?.length}
                onClick={onClick} // Call the linking function
            >
                Link Products
            </Button>
        </div>
    );
};

/**
 * ProductTable Component
 * Displays the linked products in a table with pagination.
 *
 * @param {Object} props - Component props.
 * @param {Object} props.agGridApi - API object for interacting with the AG Grid.
 * @param {Array} props.linkedProducts - List of linked products to display in the table.
 * @returns {JSX.Element} - Rendered ProductTable component.
 */
export const ProductTable = ({agGridApi, linkedProducts, onRowSelection, onOpenEditModal}) => {

    const {updateLinkedProducts} = useLinkedProductsActions();
    const {setLoadingTrue, setLoadingFalse} = useLoading();

    const onSelectionChanged = useCallback(() => {
        if (agGridApi.AgGridApiRef.current) {
            const selectedRows = agGridApi.AgGridApiRef.current.api.getSelectedRows();
            onRowSelection(selectedRows);
        }
    }, [agGridApi, onRowSelection]);

    const {section} = useSelector((state) => state.sharedSections);
    const {defaultFilterRange} = useSelector((state) => state.linkedProducts);

    const {
        setSort,
        columns
    } = useLinkedProductsGridUtils({
        datastageApi,
                section,
        defaultFilterRange,
        updateLinkedProducts,
        handlers: {
            setLoadingTrue,
            setLoadingFalse,
            columnHandlers: [
                (data) => () => onOpenEditModal(data),

            ]
        }
    });

    return (
        <div className="ag-table-wrapper">
            <div className="ag-table-container">
                <DataGrid
                    className={styles.dataGrid}
                    agGrid={{
                        ref: agGridApi.AgGridApiRef,
                        rowData: linkedProducts,
                        headerHeight: 45,
                        domLayout: 'normal',
                        rowSelection: 'multiple',
                        onSelectionChanged,
                    }}
                    bordered="none"
                    columnDefs={columns}
                    sortable={false}
                    striped
                    selectable
                />
            </div>
        </div>
    );
};

/**
 * LinkedProductsGrid Component
 * Main component to manage linked products.
 *
 * @returns {JSX.Element} - Rendered LinkedProductsGrid component.
 */
export const LinkedProductsGrid = () => {
    const agGridApi = useAgGridApi();
    const {section} = useSelector((state) => state.sharedSections);
    const {linkedProducts, productOptions, dateAddedMap, unconfirmedProductIdList, rangeParam} = useSelector(
        (state) => state.linkedProducts,
    );
    const {isMemoModalOpen} = useSelector((state) => state.titles);

    const [selectedProducts, setSelectedProducts] = useState(null);
    const [selectedRows, setSelectedRows] = useState([]);
    const {updateSharedSectionsForm} = useSharedSectionsActions();
    const {updateLinkedProducts} = useLinkedProductsActions();
    const {updateClearanceMemoTitles} = useClearanceMemoTitlesActions();
    const {openLinkedProductsDialog} = useLinkedProductsDialog();
    const {setLoadingTrue, setLoadingFalse} = useLoading();

    const loadOptions = debounce((inputValue, callback) => {
        fetchProductOptions(section?.id, inputValue, callback, updateLinkedProducts);
    }, 300);

    const handleSelectedProducts = (selectedOptions) => {
        setSelectedProducts(selectedOptions);
        const selectedProductIds = selectedOptions?.map((option) => option.value) || [];
        updateSharedSectionsForm({key: 'section', data: {productsToAdd: selectedProductIds}});
    };

    const handleRowSelection = (rows) => {
        setSelectedRows(rows);
        const selectedIds = rows?.map((row) => row.id) || [];
        updateSharedSectionsForm({key: 'section', data: {unLinkList: selectedIds}});
    };

    const handleLinkProducts = () => {
        openLinkedProductsDialog(
            'Confirm Linking',
            'Yes, Link Products',
            [`Are you sure you want to link these products to ${section?.title}?`],
            async () => {
                setLoadingTrue();
                try {
                    const updatedSection = await datastageApi.updateSharedSection(section);
                    updateSharedSections({ section: updatedSection });

                    // Fetch linked products and product options in parallel
                    const [data, productData] = await Promise.all([
                        fetchLinkedProducts(
                            section?.id,
                            rangeParam,
                            dateAddedMap,
                            unconfirmedProductIdList,
                        ),
                        fetchProducts(section?.id),
                    ]);

                    updateLinkedProducts({
                        linkedProducts: data?.linkedProducts || [],
                        totalPages: data?.totalPages,
                        currentPage: data?.currentPage,
                        totalItems: data?.totalItems,
                        productOptions: productData?.productOptions || [],
                        dateAddedMap: data?.dateAddedMap || {},
                        unconfirmedProductIdList: data?.unconfirmedProductIdList || {},
                    });
                    setSelectedProducts(null);
                } catch (error) {
                    console.error('Error linking products:', error);
                } finally {
                    setLoadingFalse();
                }
            },
        );
    };

    const handleUnlinkProducts = () => {
        openLinkedProductsDialog(
            'Confirm Unlinking',
            'Yes, Unlink Products',
            [`Are you sure you want to unlink these products from ${section?.title}?`],
            async () => {
                setLoadingTrue();
                try {
                    const updatedSection = await datastageApi.updateSharedSection(section);
                    updateSharedSections({section: updatedSection});
                    const linkedProductsData = await fetchLinkedProducts(
                        section?.id,
                        rangeParam,
                        dateAddedMap,
                        unconfirmedProductIdList,
                    );
                    updateLinkedProducts({
                        linkedProducts: linkedProductsData?.linkedProducts || [],
                        totalPages: linkedProductsData?.totalPages,
                        currentPage: linkedProductsData?.currentPage,
                        totalItems: linkedProductsData?.totalItems,
                    });
                    setSelectedRows([]);
                } catch (error) {
                    console.error('Error unlinking products:', error);
                } finally {
                    setLoadingFalse();
                }
            },
        );
    };

    const handleOpenEditModal = async (data) => {
        try {
            setLoadingTrue();

            const cmTitle = await datastageApi.titleByMappedProductId(data?.productId);
            const toc = await datastageApi.clearanceMemoTOC(cmTitle?.id);
            updateClearanceMemoTitles({
                isMemoModalOpen: true,
                title: cmTitle,
                isViewOnly: false,
                clearanceMemoTOC: toc,
                product: data,
            });
        } catch (error) {
            console.error('Error handling modal open:', error);
        } finally {
            setLoadingFalse();
        }
    };

    const handleCloseMemoModal = () => {
        // Close the Clearance Memo modal
        updateClearanceMemoTitles({
            isMemoModalOpen: false,
        });
    };

    return (
        <>
            <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: '10px'}}>
                <ProductInput
                    productOptions={productOptions}
                    loadOptions={loadOptions}
                    selectedProducts={selectedProducts}
                    onChange={handleSelectedProducts}
                    onClick={handleLinkProducts}
                />
                <Button
                    iconType="fa-solid fa-unlink"
                    labelText="Unlink Products"
                    styleType="link"
                    disabled={!selectedRows.length}
                    onClick={handleUnlinkProducts}
                >
                    Unlink Products
                </Button>
            </div>
            <ProductTable
                agGridApi={agGridApi}
                linkedProducts={linkedProducts}
                onRowSelection={handleRowSelection}
                onOpenEditModal={handleOpenEditModal}
            />
            <DetailsModal
                isOpen={isMemoModalOpen}
                onClose={handleCloseMemoModal}
                modalHeader="Clearance Memo"
                footer={
                    <Button sizeType="small" onClick={handleCloseMemoModal}>
                        Close
                    </Button>
                }
            >
                <MemoForm />
            </DetailsModal>
            <EditSectionModal />
        </>
    );
};
